'use client'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useWallet } from '@/providers/WalletContext'
import { captureApiError } from '@/utils/sentry-helpers'
import { useLoginWithEmail, useLoginWithOAuth } from '@privy-io/react-auth'
import { useCallback, useEffect, useState } from 'react'
import { useSiweAuth } from './hooks'
import { EmailStep, OtpStep, WalletSigningStep } from './steps'
import type { AuthStep } from './types'
import { isRateLimitError, parseOtpError } from './utils'

type AuthDialogProps = {
	open: boolean
	onOpenChange: (open: boolean) => void
}

function getErrorMessage(err: unknown): string {
	return err instanceof Error ? err.message : String(err)
}

export function AuthDialog({ open, onOpenChange }: AuthDialogProps) {
	const [step, setStep] = useState<AuthStep>('email')
	const [email, setEmail] = useState('')
	const [otpCode, setOtpCode] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [isGoogleLoading, setIsGoogleLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const { authenticated } = useWallet()

	// SIWE wallet auth
	const {
		connect: connectWallet,
		cancel: cancelWallet,
		isPending: isWalletPending,
		step: walletStep,
		error: walletError,
		clearError: clearWalletError,
	} = useSiweAuth()

	const resetState = useCallback(() => {
		setStep('email')
		setEmail('')
		setOtpCode('')
		setError(null)
		setIsLoading(false)
		setIsGoogleLoading(false)
		cancelWallet()
	}, [cancelWallet])

	const handleClose = useCallback(
		(newOpen: boolean) => {
			if (!newOpen) {
				resetState()
			}
			onOpenChange(newOpen)
		},
		[resetState, onOpenChange]
	)

	const { sendCode, loginWithCode } = useLoginWithEmail({
		onComplete: () => handleClose(false),
	})

	// Web OAuth is a full-page redirect — the dialog's local state does not
	// survive the round-trip. This hook must stay mounted on the page users
	// return to (the dialog lives in the root-mounted global modals), where
	// onError surfaces post-redirect failures.
	const { initOAuth } = useLoginWithOAuth({
		onComplete: () => handleClose(false),
		onError: code => {
			setIsGoogleLoading(false)
			const codeStr = String(code)
			// User backing out of the Google screen isn't an error worth surfacing.
			if (codeStr === 'oauth_user_denied' || codeStr === 'exited_auth_flow') {
				return
			}
			setError('Failed to sign in with Google')
			captureApiError(codeStr, {
				endpoint: 'google_auth',
			})
		},
	})

	// Safety net: if a session appears while the dialog is open (e.g. SIWE
	// bridge sync or an OAuth return with the dialog re-opened), close it.
	useEffect(() => {
		if (authenticated && open) {
			handleClose(false)
		}
	}, [authenticated, open, handleClose])

	const handleSendCode = async () => {
		if (!email) {
			return
		}
		setError(null)
		clearWalletError()
		setIsLoading(true)

		try {
			await sendCode({ email })
			setStep('otp')
		} catch (err) {
			const errorMessage = getErrorMessage(err)
			if (isRateLimitError(errorMessage)) {
				setError('Too many attempts. Please wait a moment and try again.')
			} else {
				setError(errorMessage || 'Failed to send verification code')
			}
		} finally {
			setIsLoading(false)
		}
	}

	const submitOtp = async (code: string) => {
		setError(null)
		setIsLoading(true)
		try {
			await loginWithCode({ code })
			// onComplete closes the dialog.
		} catch (err) {
			setError(parseOtpError(getErrorMessage(err)))
		} finally {
			setIsLoading(false)
		}
	}

	const handleVerifyCode = () => {
		if (!otpCode || otpCode.length !== 6) {
			return
		}
		submitOtp(otpCode)
	}

	const handleAutoSubmitOtp = (code: string) => {
		submitOtp(code)
	}

	const handleBack = () => {
		if (step === 'otp') {
			setStep('email')
			setOtpCode('')
		}
		setError(null)
	}

	const handleResendCode = () => {
		setOtpCode('')
		setError(null)
		handleSendCode()
	}

	const handleGoogleLogin = async () => {
		setError(null)
		clearWalletError()
		setIsGoogleLoading(true)
		try {
			// Full-page redirect — keep the loading state until navigation.
			await initOAuth({ provider: 'google' })
		} catch (err) {
			setIsGoogleLoading(false)
			setError(getErrorMessage(err) || 'Failed to sign in with Google')
		}
	}

	const handleWalletConnect = async () => {
		setError(null)
		clearWalletError()
		const success = await connectWallet()
		if (success) {
			onOpenChange(false)
			resetState()
		}
	}

	const lockCloseOnOutside = walletStep !== 'idle'

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				closeButtonClasses="size-[24px] text-brand-gray-secondary"
				closeButtonContainerClasses="top-[30px] cursor-pointer"
				className="gap-[32px] rounded-3xl bg-brand-dark-light p-[24px] sm:max-w-[420px]"
				onInteractOutside={e => {
					// AppKit opens its modal in a sibling portal; clicks inside it
					// register as "outside" this dialog and would normally close it —
					// which tears down our in-flight SIWE flow via resetState.
					if (lockCloseOnOutside) e.preventDefault()
				}}
				onEscapeKeyDown={e => {
					if (lockCloseOnOutside) e.preventDefault()
				}}
			>
				<DialogTitle className="sr-only">Sign in</DialogTitle>
				{(walletStep === 'signing' || walletStep === 'verifying') && (
					<WalletSigningStep step={walletStep} error={walletError} />
				)}
				{step === 'email' && walletStep !== 'signing' && walletStep !== 'verifying' && (
					<EmailStep
						email={email}
						setEmail={setEmail}
						onSubmit={handleSendCode}
						isLoading={isLoading}
						error={error}
						onGoogleLogin={handleGoogleLogin}
						isGoogleLoading={isGoogleLoading}
						onWalletConnect={handleWalletConnect}
						isWalletPending={isWalletPending}
						walletStep={walletStep}
						walletError={walletError}
					/>
				)}
				{step === 'otp' && (
					<OtpStep
						email={email}
						otpCode={otpCode}
						setOtpCode={setOtpCode}
						onSubmit={handleVerifyCode}
						onBack={handleBack}
						onResend={handleResendCode}
						onAutoSubmit={handleAutoSubmitOtp}
						isLoading={isLoading}
						error={error}
					/>
				)}
			</DialogContent>
		</Dialog>
	)
}
